import { ErrorDetailsHttp, ErrorJSON } from '../errors';

export class PlaidError extends Error {
    details?: PlaidErrorDetails;
    extensions?: PlaidErrorExtensions;

    constructor(
        message: string,
        details?: PlaidErrorDetails,
        extensions?: PlaidErrorExtensions
    ) {
        super(message);
        this.name = this.constructor.name;
        this.details = details;
        this.extensions = extensions;
        // TypeScript requires a workaround when extending built-ins like Error
        // Ref: https://github.com/Microsoft/TypeScript-wiki/blob/main/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
        Object.setPrototypeOf(this, PlaidError.prototype);
    }

    toJSON(): ErrorJSON {
        return {
            name: this.name,
            message: this.message,
            stack: this.stack,
            details: this.details,
            extensions: this.extensions,
        };
    }
}

export type PlaidErrorExtensions = PlaidErrorTypeAndCode;

export interface PlaidErrorDetails extends Record<string, unknown> {
    body: PlaidErrorResponseBody;
    http: ErrorDetailsHttp;
}

export type PlaidErrorResponseBody = PlaidErrorTypeAndCode & {
    /** A developer-friendly representation of the error code. This may change over time and is not safe for programmatic use. */
    error_message: string;

    /** A user-friendly representation of the error code. `null` if the error is not related to user action. This may change over time and is not safe for programmatic use. */
    display_message?: string | null;

    /** A unique ID identifying the request, to be used for troubleshooting purposes. This field will be omitted in errors provided by webhooks. */
    request_id?: string | null;

    // `causes` is only returned from the Assets product, so it's not currently relevant to us.
    // causes?: unknown[]

    /** The HTTP status code associated with the error. This will only be returned in the response body when the error information is provided via a webhook. */
    status?: number | null;

    /** The URL of a Plaid documentation page with more information about the error */
    documentation_url?: string | null;

    /** Suggested steps for resolving the error */
    suggestedAction?: string | null;
};

export type PlaidErrorTypeAndCode =
    | PlaidInstitutionErrorTypeAndCode
    | PlaidItemErrorTypeAndCode;

type PlaidItemErrorTypeAndCode = {
    error_type: 'ITEM_ERROR';
    error_code:
        | 'ACCESS_NOT_GRANTED'
        | 'INSTANT_MATCH_FAILED'
        | 'INSUFFICIENT_CREDENTIALS'
        | 'INVALID_CREDENTIALS'
        | 'INVALID_MFA'
        | 'INVALID_SEND_METHOD'
        | 'INVALID_UPDATED_USERNAME'
        | 'ITEM_CONCURRENTLY_DELETED'
        | 'ITEM_LOCKED'
        | 'ITEM_LOGIN_REQUIRED'
        | 'ITEM_NOT_FOUND'
        | 'ITEM_NOT_SUPPORTED'
        | 'MFA_NOT_SUPPORTED'
        | 'NO_ACCOUNTS'
        | 'NO_AUTH_ACCOUNTS'
        | 'no-depository-accounts'
        | 'NO_INVESTMENT_ACCOUNTS'
        | 'NO_INVESTMENT_AUTH_ACCOUNTS'
        | 'NO_LIABILITY_ACCOUNTS'
        | 'PRODUCT_NOT_ENABLED'
        | 'PRODUCT_NOT_READY'
        | 'PRODUCTS_NOT_SUPPORTED'
        | 'USER_INPUT_TIMEOUT'
        | 'USER_SETUP_REQUIRED';
};

interface PlaidInstitutionErrorTypeAndCode extends Record<string, unknown> {
    error_type: 'INSTITUTION_ERROR';
    error_code:
        | 'INSTITUTION_DOWN'
        | 'INSTITUTION_NO_LONGER_SUPPORTED'
        | 'INSTITUTION_NOT_AVAILABLE'
        | 'INSTITUTION_NOT_ENABLED_IN_ENVIRONMENT'
        | 'INSTITUTION_NOT_FOUND'
        | 'INSTITUTION_NOT_RESPONDING'
        | 'INSTITUTION_REGISTRATION_REQUIRED'
        | 'UNAUTHORIZED_INSTITUTION';
}
